import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Leaf, Save, Send, Download, Check, Undo2, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const STATUT_CLS = {
  brouillon: "bg-slate-50 text-slate-700 border-slate-300",
  declare: "bg-amber-50 text-amber-800 border-amber-300",
  a_corriger: "bg-red-50 text-red-700 border-red-300",
  valide: "bg-emerald-50 text-emerald-700 border-emerald-300",
};
const erreur = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);
const nombre = (v) => (v === null || v === undefined ? "-" : String(v).replace(".", ","));

function Onglet({ actif, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-1.5 text-sm rounded-md ${actif ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted"}`}>
      {children}
    </button>
  );
}

export default function BilanRSEPage() {
  const { user } = useAuth();
  const gestion = ["admin", "bureau"].includes(user?.role);
  const aUnCamion = !!user?.food_truck_id;
  const [onglet, setOnglet] = useState(aUnCamion ? "mien" : "tous");
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [ref, setRef] = useState(null);

  useEffect(() => {
    axios.get(`${API}/rse/referentiel`).then((r) => setRef(r.data)).catch(() => toast.error("Chargement impossible"));
  }, []);

  if (!ref) return null;
  return (
    <div className="space-y-4" data-testid="bilan-rse">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-secondary flex items-center gap-2">
            <Leaf className="w-6 h-6 text-primary" /> Bilan RSE
          </h1>
          <p className="text-sm text-muted-foreground">
            Une fois par an, chaque food truck signataire de la charte fait le point sur ses engagements et quelques chiffres.
            Le bureau valide chaque bilan avant qu'il ne compte dans les résultats de l'association.
          </p>
        </div>
        <div><Label className="text-xs">Année</Label>
          <select className="h-9 border rounded-md px-2 text-sm block" value={annee} onChange={(e) => setAnnee(parseInt(e.target.value, 10))}>
            {[0, 1, 2, 3].map((k) => new Date().getFullYear() - k).map((a) => <option key={a} value={a}>{a}</option>)}
          </select></div>
      </div>
      {aUnCamion && gestion && (
        <div className="flex gap-1">
          <Onglet actif={onglet === "mien"} onClick={() => setOnglet("mien")}>Mon bilan</Onglet>
          <Onglet actif={onglet === "tous"} onClick={() => setOnglet("tous")}>Bilans des food trucks</Onglet>
        </div>
      )}
      {onglet === "mien" && aUnCamion && <MonBilan annee={annee} ref_={ref} />}
      {onglet === "tous" && gestion && <TousLesBilans annee={annee} ref_={ref} />}
      {!aUnCamion && !gestion && (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Votre compte n'est rattaché à aucun food truck.</CardContent></Card>
      )}
    </div>
  );
}

function MonBilan({ annee, ref_ }) {
  const [b, setB] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/rse/mon-bilan`, { params: { annee } });
      setB(r.data);
    } catch (e) { toast.error(erreur(e, "Chargement impossible")); }
  }, [annee]);
  useEffect(() => { charger(); }, [charger]);

  if (!b) return null;
  if (!b.charte_signee) {
    return (
      <Card><CardContent className="p-6 space-y-3 text-center">
        <p>Le bilan porte sur les engagements que vous avez choisis en signant la charte RSE.</p>
        <Link to="/charte-rse"><Button><Leaf className="w-4 h-4 mr-1" /> Signer la charte RSE</Button></Link>
      </CardContent></Card>
    );
  }
  const modifiable = ["brouillon", "a_corriger"].includes(b.statut);
  const setInd = (code, v) => setB((x) => ({ ...x, indicateurs: { ...x.indicateurs, [code]: v } }));
  const setPro = (code, champ, v) => setB((x) => ({ ...x, progres: { ...x.progres, [code]: { ...x.progres[code], [champ]: v } } }));

  const enregistrer = async (puisTransmettre) => {
    setEnvoi(true);
    try {
      const r = await axios.put(`${API}/rse/mon-bilan`, { indicateurs: b.indicateurs, progres: b.progres, commentaire: b.commentaire }, { params: { annee } });
      setB(r.data);
      if (puisTransmettre) {
        if (!window.confirm("Transmettre votre bilan au bureau ? Vous ne pourrez plus le modifier, sauf si le bureau vous le renvoie.")) { setEnvoi(false); return; }
        const t = await axios.post(`${API}/rse/mon-bilan/declarer`, null, { params: { annee } });
        setB(t.data);
        toast.success("Bilan transmis au bureau");
      } else {
        toast.success("Brouillon enregistré");
      }
    } catch (e) { toast.error(erreur(e, "Enregistrement impossible")); }
    setEnvoi(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className={STATUT_CLS[b.statut]}>{ref_.statuts[b.statut]}</Badge>
        {b.statut === "a_corriger" && b.motif_correction && <span className="text-sm text-red-700">Demande du bureau : {b.motif_correction}</span>}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Mes engagements de progrès</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {Object.keys(b.progres).length === 0 && <p className="text-sm text-muted-foreground">Aucun engagement de progrès choisi à la signature.</p>}
          {Object.entries(b.progres).map(([code, p]) => (
            <div key={code} className="border rounded-md p-3 space-y-2">
              <p className="text-sm font-medium">{p.libelle}</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(ref_.etats_progres).map(([k, v]) => (
                  <Button key={k} type="button" size="sm" disabled={!modifiable}
                    variant={p.etat === k ? "default" : "outline"} onClick={() => setPro(code, "etat", k)}>{v}</Button>
                ))}
              </div>
              <Input placeholder="Où en êtes-vous ? (facultatif)" value={p.note || ""} disabled={!modifiable}
                onChange={(e) => setPro(code, "note", e.target.value)} />
            </div>
          ))}
        </CardContent>
      </Card>

      {Object.entries(ref_.themes).map(([th, nom]) => (
        <Card key={th}>
          <CardHeader className="pb-2"><CardTitle className="text-base">{nom}</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ref_.indicateurs.filter((i) => i.theme === th).map((i) => (
              <div key={i.code}>
                <Label className="text-sm">{i.libelle}{i.unite ? ` (${i.unite})` : ""}</Label>
                {i.type === "oui_non" ? (
                  <select className="w-full h-9 border rounded-md px-2 text-sm mt-1" disabled={!modifiable}
                    value={b.indicateurs[i.code] === true ? "oui" : b.indicateurs[i.code] === false ? "non" : ""}
                    onChange={(e) => setInd(i.code, e.target.value === "" ? null : e.target.value === "oui")}>
                    <option value="">Non renseigné</option><option value="oui">Oui</option><option value="non">Non</option>
                  </select>
                ) : (
                  <Input type="number" className="mt-1" disabled={!modifiable}
                    value={b.indicateurs[i.code] ?? ""} placeholder={i.type === "pct" ? "0 à 100" : ""}
                    onChange={(e) => setInd(i.code, e.target.value === "" ? null : e.target.value)} />
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      <Card><CardContent className="p-4 space-y-2">
        <Label>Commentaire pour le bureau (facultatif)</Label>
        <Textarea rows={3} value={b.commentaire || ""} disabled={!modifiable} onChange={(e) => setB((x) => ({ ...x, commentaire: e.target.value }))} />
        <p className="text-xs text-muted-foreground">
          Laissez vide ce que vous ne mesurez pas : une case vide ne compte pas dans les moyennes de l'association.
          Gardez vos justificatifs (bons de collecte d'huile, factures), le bureau peut vous les demander.
        </p>
      </CardContent></Card>

      {modifiable && (
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" disabled={envoi} onClick={() => enregistrer(false)}><Save className="w-4 h-4 mr-1" /> Enregistrer le brouillon</Button>
          <Button disabled={envoi} onClick={() => enregistrer(true)}><Send className="w-4 h-4 mr-1" /> Transmettre au bureau</Button>
        </div>
      )}
    </div>
  );
}

function TousLesBilans({ annee, ref_ }) {
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState(null);
  const [corriger, setCorriger] = useState(null);
  const [motif, setMotif] = useState("");

  const charger = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/rse/bilans`, { params: { annee } });
      setData(r.data);
    } catch (e) { toast.error(erreur(e, "Chargement impossible")); }
  }, [annee]);
  useEffect(() => { charger(); }, [charger]);

  const decider = async (id, decision, m = "") => {
    try {
      await axios.post(`${API}/rse/bilans/${id}/decision`, { decision, motif: m });
      toast.success(decision === "valider" ? "Bilan validé" : "Bilan renvoyé au food truck");
      setCorriger(null); setMotif("");
      charger();
    } catch (e) { toast.error(erreur(e, "Action impossible")); }
  };

  const pdf = async () => {
    try {
      const r = await axios.get(`${API}/rse/synthese/pdf`, { params: { annee }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `bilan_rse_${annee}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF indisponible"); }
  };

  if (!data) return null;
  const s = data.synthese;
  const ind = s.indicateurs;
  const tuiles = [
    [s.nb_signataires, "food trucks signataires de la charte"],
    [s.nb_bilans_valides, `bilans validés en ${annee}`],
    [ind.achats_locaux_pct.valeur !== null ? `${nombre(ind.achats_locaux_pct.valeur)} %` : "-", "d'achats locaux en moyenne"],
    [ind.huile_litres.valeur !== null ? `${nombre(ind.huile_litres.valeur)} L` : "-", "d'huile recyclée"],
    [ind.invendus_kg.valeur !== null ? `${nombre(ind.invendus_kg.valeur)} kg` : "-", "d'invendus valorisés"],
    [`${s.engagements_atteints} / ${s.engagements_suivis}`, "engagements de progrès atteints"],
  ];
  const enAttente = data.lignes.filter((l) => l.bilan?.statut === "declare").length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {tuiles.map(([v, l]) => (
          <Card key={l}><CardContent className="p-3">
            <p className="text-xl font-bold text-secondary">{v}</p>
            <p className="text-xs text-muted-foreground">{l}</p>
          </CardContent></Card>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Chiffres calculés sur les seuls bilans validés. Ils apparaissent dans le dossier de présentation
          dès {ref_.minimum_dossier} bilans validés sur une même année.
          {enAttente ? <strong className="text-amber-700"> {enAttente} bilan(s) à valider.</strong> : null}
        </p>
        <Button variant="outline" onClick={pdf}><Download className="w-4 h-4 mr-1" /> Bilan RSE {annee} (PDF)</Button>
      </div>

      <Card><CardContent className="p-0 divide-y">
        {data.lignes.map((l) => {
          const b = l.bilan;
          const vu = ouvert === l.food_truck_id;
          return (
            <div key={l.food_truck_id} className="p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium flex-1 min-w-[160px]">{l.nom}</p>
                {!l.charte_signee && <Badge variant="outline" className="text-[11px]">Charte non signée</Badge>}
                <Badge variant="outline" className={STATUT_CLS[b?.statut || "brouillon"]}>
                  {b ? ref_.statuts[b.statut] : "Pas de bilan"}
                </Badge>
                {b && (
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => setOuvert(vu ? null : l.food_truck_id)}>
                    {vu ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} Détail
                  </Button>
                )}
                {b && ["declare", "valide"].includes(b.statut) && (
                  <>
                    {b.statut === "declare" && (
                      <Button size="sm" className="h-7" onClick={() => decider(b.id, "valider")}><Check className="w-3.5 h-3.5 mr-1" /> Valider</Button>
                    )}
                    <Button size="sm" variant="outline" className="h-7" onClick={() => { setCorriger(b); setMotif(""); }}>
                      <Undo2 className="w-3.5 h-3.5 mr-1" /> Renvoyer
                    </Button>
                  </>
                )}
              </div>
              {vu && b && (
                <div className="text-sm grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 bg-muted/30 rounded-md p-3">
                  {ref_.indicateurs.map((i) => (
                    <p key={i.code}><span className="text-muted-foreground">{i.libelle} : </span>
                      <strong>{i.type === "oui_non"
                        ? (b.indicateurs?.[i.code] === true ? "oui" : b.indicateurs?.[i.code] === false ? "non" : "-")
                        : `${nombre(b.indicateurs?.[i.code])}${b.indicateurs?.[i.code] != null && i.unite ? " " + i.unite : ""}`}</strong></p>
                  ))}
                  <div className="md:col-span-2 pt-2 space-y-1">
                    {Object.values(b.progres_detail || {}).map((p) => (
                      <p key={p.libelle}>• {p.libelle} : <strong>{ref_.etats_progres[p.etat]}</strong>{p.note ? ` (${p.note})` : ""}</p>
                    ))}
                    {b.commentaire && <p className="italic text-muted-foreground">« {b.commentaire} »</p>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent></Card>

      <Dialog open={!!corriger} onOpenChange={(o) => { if (!o) setCorriger(null); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Renvoyer le bilan pour correction</DialogTitle></DialogHeader>
          <div><Label>Ce qu'il faut corriger (transmis au food truck)</Label>
            <Textarea rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Litres d'huile à justifier, pourcentage à vérifier..." /></div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCorriger(null)}>Annuler</Button>
            <Button onClick={() => decider(corriger.id, "corriger", motif)}>Renvoyer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
